import { useState, useCallback } from 'react';

const useItemCache = () => {
    const [itemCache, setItemCache] = useState({});
    const [loadingItems, setLoadingItems] = useState(new Set());

    const loadItemData = useCallback(async (itemId) => {
        if (!itemId) return null;

        if (itemCache[itemId]) {
            return itemCache[itemId];
        }

        if (loadingItems.has(itemId)) {
            return null;
        }

        setLoadingItems(prev => new Set(prev).add(itemId));

        try {
            const response = await fetch(`https://api.deadlock-api.com/v1/assets/items/${itemId}`);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const itemData = await response.json();

            const itemInfo = {
                id: itemData.id,
                name: itemData.name,
                image: itemData.image,
                shop_image: itemData.shop_image,
                shop_image_small: itemData.shop_image_small,
                cost: itemData.cost || 0,
                type: itemData.type,
                description: {
                    desc: itemData.description?.desc || '',
                    active: itemData.description?.active || '',
                    passive: itemData.description?.passive || ''
                },
                properties: itemData.properties || {},
                activation: itemData.activation || 'passive',
                item_slot_type: itemData.item_slot_type || '',
                item_tier: itemData.item_tier || 1,
                cooldown: itemData.properties?.AbilityCooldown?.value || null,
                tooltip_sections: itemData.tooltip_sections || []
            };

            setItemCache(prev => ({
                ...prev,
                [itemId]: itemInfo
            }));

            return itemInfo;
        } catch {
            return null;
        } finally {
            setLoadingItems(prev => {
                const newSet = new Set(prev);
                newSet.delete(itemId);
                return newSet;
            });
        }
    }, [itemCache, loadingItems]);

    const loadItemsForPlayer = useCallback(async (upgrades) => {
        if (!upgrades || !Array.isArray(upgrades)) return [];

        const items = [];
        for (const upgradeId of upgrades) {
            const itemData = await loadItemData(upgradeId);
            if (itemData) {
                items.push(itemData);
            }
        }

        return items.sort((a, b) => (a.cost || 0) - (b.cost || 0));
    }, [loadItemData]);

    return { itemCache, loadItemData, loadItemsForPlayer, loadingItems };
};

export default useItemCache;
